import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';

/**
 * Interface for card data structure
 */
export interface CardData {
  title: string;
  description: string;
  time?: Date | string;
  icon?: string;
  type?: string;
  isLoading?: boolean;
  threadId?: string;
  [key: string]: any; // Allow additional custom properties
}

/**
 * GenericCardComponent - A reusable card component for displaying test case information
 * Displays title, description, and last edited timestamp with formatted relative time
 */
@Component({
  selector: 'app-generic-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './generic-card.component.html',
  styleUrl: './generic-card.component.scss'
})
export class GenericCardComponent {
  /**
   * Input property for card data
   * Accepts CardData interface with title, description, and optional lastEdited date
   */
  @Input() cardData!: CardData;
  /**
   * Input property to show a loading spinner on the card.
   * Defaults to false.
   */
  @Input() isLoading = false;

  /**
   * Input to control the visibility of the edit button.
   * Defaults to false.
   */
  @Input() showEditButton = false;

  /**
   * Output event emitter for the edit button click.
   */
  @Output() editClick = new EventEmitter<CardData>();
  /**
   * Output event emitter for card click
   * Emits the card data when the card is clicked
   */
  @Output() cardClick = new EventEmitter<CardData>();

  /**
   * Handles the click event on the card and emits the card data.
   */
  onCardClick(): void {
    if (!this.isLoading) {
      this.cardClick.emit(this.cardData);
    }
  }

  /**
   * Handles the click event on the edit icon, stops event propagation,
   * and emits the editClick event.
   */
  onEditClick(event: MouseEvent): void {
    event.stopPropagation(); // Prevents the onCardClick from firing
    this.editClick.emit(this.cardData);
  }
  /**
   * Formats the date into a human-readable relative time string
   * Examples: "Edited today", "Edited 1 day ago", "Edited 10 days ago"
   * @param date - The date to format (can be Date object or ISO string)
   * @returns Formatted time string
   */
  formatEditedTime(date: Date | string | undefined): string {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Edited today';
    } else if (diffDays === 1) {
      return 'Edited 1 day ago';
    } else if (diffDays < 30) {
      return `Edited ${diffDays} days ago`;
    } else {
      return dateObj.toLocaleDateString();
    }
  }

  getImage(type:string){
    switch(type) {
      case 'design':
        return 'assets/images/virtusa-icons/design-btn.svg';
      case 'execution':
        return 'assets/images/virtusa-icons/execution-btn.svg';
      case 'analytics':
        return 'assets/images/virtusa-icons/analytics-btn.svg';
      case 'maintenance':
        return 'assets/images/virtusa-icons/maintenance-btn.svg';
      default:
        return '';
    }
  }
}
